// MailRules.js - Organization-wide and User-level Email Rules Management
import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';
import {
  FunnelIcon, PlusIcon, PencilIcon, TrashIcon, XMarkIcon,
  CheckIcon, ArrowPathIcon
} from '@heroicons/react/24/outline';
import { FunnelIcon as FunnelIconSolid } from '@heroicons/react/24/solid';
import Card from '../ui/Card';
import Button from '../ui/Button';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import logger from '../../utils/logger';

const MailRules = ({ showNotification }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [templates, setTemplates] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    scope: 'inbound',
    appliesTo: 'allUsers',
    domains: [],
    conditions: [{ id: crypto.randomUUID(), field: 'from', operator: 'contains', value: '' }],
    actions: [{ id: crypto.randomUUID(), type: 'moveToFolder', value: 'inbox' }],
    enabled: true,
    priority: 50
  });

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const [orgRes, templatesRes] = await Promise.all([
        axiosInstance.get('/api/business/mail-rules/organization').catch(() => ({ data: { success: false, rules: [] } })),
        axiosInstance.get('/api/business/mail-rules/templates').catch(() => ({ data: { success: false, templates: [] } }))
      ]);

      if (orgRes.data.success) setRules(orgRes.data.rules);
      if (templatesRes.data.success) setTemplates(templatesRes.data.templates);
    } catch (error) {
      logger.error('Error fetching rules:', error);
      showNotification?.('Failed to load mail rules', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleSaveRule = async () => {
    try {
      const endpoint = '/api/business/mail-rules/organization';
      const method = editingRule ? 'put' : 'post';
      const url = editingRule ? `${endpoint}/${editingRule._id}` : endpoint;

      const res = await axiosInstance[method](url, formData);

      if (res.data.success) {
        showNotification?.(`Rule ${editingRule ? 'updated' : 'created'} successfully`, 'success');
        fetchRules();
        setShowModal(false);
        resetForm();
      }
    } catch (error) {
      showNotification?.(error.response?.data?.message || 'Failed to save rule', 'error');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;

    try {
      await axiosInstance.delete(`/api/business/mail-rules/organization/${ruleId}`);
      showNotification?.('Rule deleted successfully', 'success');
      fetchRules();
    } catch (error) {
      showNotification?.('Failed to delete rule', 'error');
    }
  };

  const handleToggleRule = async (ruleId, currentStatus) => {
    try {
      await axiosInstance.patch(`/api/business/mail-rules/organization/${ruleId}/toggle`);
      showNotification?.(`Rule ${!currentStatus ? 'enabled' : 'disabled'}`, 'success');
      fetchRules();
    } catch (error) {
      showNotification?.('Failed to toggle rule', 'error');
    }
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      scope: rule.scope,
      appliesTo: rule.appliesTo || 'allUsers',
      domains: rule.domains || [],
      conditions: rule.conditions,
      actions: rule.actions,
      enabled: rule.enabled,
      priority: rule.priority
    });
    setShowModal(true);
  };

  const handleUseTemplate = (template) => {
    setFormData({
      ...template.template,
      priority: 50,
      enabled: true
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      scope: 'inbound',
      appliesTo: 'allUsers',
      domains: [],
      conditions: [{ field: 'from', operator: 'contains', value: '' }],
      actions: [{ type: 'moveToFolder', value: 'inbox' }],
      enabled: true,
      priority: 50
    });
  };

  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { id: crypto.randomUUID(), field: 'from', operator: 'contains', value: '' }]
    }));
  };

  const removeCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };

  const updateCondition = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };

  const addAction = () => {
    setFormData(prev => ({
      ...prev,
      actions: [...prev.actions, { id: crypto.randomUUID(), type: 'moveToFolder', value: 'inbox' }]
    }));
  };

  const removeAction = (index) => {
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };

  const updateAction = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.map((a, i) => i === index ? { ...a, [field]: value } : a)
    }));
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111] overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FunnelIconSolid className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white">Mail Rules</h1>
            <span className="px-1.5 py-0.5 text-[11px] font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-full">
              {rules.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchRules}
              className="p-1.5 rounded-md bg-gray-100 dark:bg-[#222222] hover:bg-gray-200 dark:hover:bg-[#2a2a2a] transition-colors"
            >
              <ArrowPathIcon className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
            </button>
            <Button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              disabled={rules.length >= 100}
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              New Rule
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin" />
          </div>
        ) : rules.length === 0 ? (
          <Card className="p-12 text-center">
            <FunnelIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No rules yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Create your first organization mail rule to automate email processing
            </p>

            {templates.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Quick start templates:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {templates.filter(t => t.type === 'both' || t.type === 'organization').map(template => (
                    <button
                      key={template.id}
                      onClick={() => handleUseTemplate(template)}
                      className="p-4 bg-gray-50 dark:bg-[#1a1a1a] rounded-lg text-left hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors border border-gray-200 dark:border-gray-800"
                    >
                      <h5 className="text-sm font-medium text-gray-900 dark:text-white">{template.name}</h5>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{template.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Card>
        ) : (
          <div className="space-y-3">
            {rules.map(rule => (
              <Card key={rule._id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-base font-medium text-gray-900 dark:text-white">{rule.name}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        rule.enabled
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-[#222222] dark:text-gray-400'
                      }`}>
                        {rule.enabled ? 'Active' : 'Disabled'}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-full">
                        Priority: {rule.priority}
                      </span>
                    </div>

                    {rule.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{rule.description}</p>
                    )}

                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="px-2 py-1 bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 rounded">
                        {rule.scope === 'all' ? 'All emails' : rule.scope === 'inbound' ? 'Inbound' : 'Outbound'}
                      </span>
                      <span className="px-2 py-1 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded">
                        {rule.conditions?.length || 0} condition(s)
                      </span>
                      <span className="px-2 py-1 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded">
                        {rule.actions?.length || 0} action(s)
                      </span>
                      {rule.matchCount > 0 && (
                        <span className="px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded">
                          Matched {rule.matchCount} times
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleToggleRule(rule._id, rule.enabled)}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors"
                      title={rule.enabled ? 'Disable rule' : 'Enable rule'}
                    >
                      {rule.enabled ? (
                        <CheckIcon className="w-5 h-5 text-green-600" />
                      ) : (
                        <XMarkIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </button>

                    <button
                      onClick={() => handleEditRule(rule)}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors"
                      title="Edit rule"
                    >
                      <PencilIcon className="w-5 h-5 text-blue-600" />
                    </button>

                    <button
                      onClick={() => handleDeleteRule(rule._id)}
                      className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors"
                      title="Delete rule"
                    >
                      <TrashIcon className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingRule ? 'Edit Rule' : 'Create New Rule'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#222222] transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Rule Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
                    placeholder="e.g., Block spam from example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
                    placeholder="Optional description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Scope *
                    </label>
                    <select
                      value={formData.scope}
                      onChange={(e) => setFormData(prev => ({ ...prev, scope: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
                    >
                      <option value="inbound">Inbound</option>
                      <option value="outbound">Outbound</option>
                      <option value="all">All</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Priority *
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 1 }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Conditions */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    When (Conditions) *
                  </label>
                  <button
                    onClick={addCondition}
                    className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                  >
                    + Add Condition
                  </button>
                </div>

                <div className="space-y-3">
                  {formData.conditions.map((condition, index) => (
                    <div key={condition.id || index} className="flex items-start space-x-2 p-3 bg-gray-50 dark:bg-[#2a2a2a] rounded-lg">
                      <select
                        value={condition.field}
                        onChange={(e) => updateCondition(index, 'field', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#222222] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                      >
                        <option value="from">From</option>
                        <option value="to">To</option>
                        <option value="subject">Subject</option>
                        <option value="body">Body</option>
                        <option value="hasAttachment">Has Attachment</option>
                        <option value="attachmentName">Attachment Name</option>
                      </select>

                      <select
                        value={condition.operator}
                        onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#222222] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                      >
                        <option value="contains">Contains</option>
                        <option value="equals">Equals</option>
                        <option value="endsWith">Ends with</option>
                      </select>

                      {condition.field !== 'hasAttachment' && (
                        <input
                          type="text"
                          value={condition.value}
                          onChange={(e) => updateCondition(index, 'value', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#222222] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                          placeholder="Value..."
                        />
                      )}

                      {formData.conditions.length > 1 && (
                        <button
                          onClick={() => removeCondition(index)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Then Do (Actions) *
                  </label>
                  <button
                    onClick={addAction}
                    className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                  >
                    + Add Action
                  </button>
                </div>

                <div className="space-y-3">
                  {formData.actions.map((action, index) => (
                    <div key={action.id || index} className="flex items-start space-x-2 p-3 bg-gray-50 dark:bg-[#2a2a2a] rounded-lg">
                      <select
                        value={action.type}
                        onChange={(e) => updateAction(index, 'type', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#222222] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                      >
                        <option value="forward">Forward to email</option>
                        <option value="moveToFolder">Move to folder</option>
                        <option value="addSubjectPrefix">Add subject prefix</option>
                        <option value="block">Block/Delete</option>
                        <option value="markAsRead">Mark as read</option>
                        <option value="markAsJunk">Mark as junk</option>
                        <option value="cc">CC another address</option>
                      </select>

                      {['forward', 'moveToFolder', 'addSubjectPrefix', 'cc'].includes(action.type) && (
                        action.type === 'moveToFolder' ? (
                          <select
                            value={action.value}
                            onChange={(e) => updateAction(index, 'value', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#222222] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                          >
                            <option value="inbox">Inbox</option>
                            <option value="junk">Junk</option>
                            <option value="archived">Archived</option>
                            <option value="deleted">Deleted</option>
                          </select>
                        ) : (
                          <input
                            type="text"
                            value={action.value}
                            onChange={(e) => updateAction(index, 'value', e.target.value)}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#222222] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                            placeholder={action.type === 'forward' || action.type === 'cc' ? 'email@example.com' : 'Prefix text...'}
                          />
                        )
                      )}

                      {formData.actions.length > 1 && (
                        <button
                          onClick={() => removeAction(index)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-gray-50 dark:bg-[#2a2a2a] border-t border-gray-200 dark:border-gray-800 px-6 py-4 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] rounded-lg transition-colors"
              >
                Cancel
              </button>
              <Button
                onClick={handleSaveRule}
                disabled={!formData.name || formData.conditions.length === 0 || formData.actions.length === 0}
              >
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailRules;
